import { v4 as guid } from "uuid";
import { call, takeLatest, put, takeEvery } from "redux-saga/effects";
import { baseURL } from "./apis";
import * as T from "../redux/constants";
import { getAuthCredentials } from "../utils/getCognitoToken";
import apigClientFactory from "aws-api-gateway-client";
import { Logger } from "../Logger/logger";
import { NETWORK_ERROR, USER_NOT_FOUND_ERROR } from "../redux/Errors/constants";

function getMockData(mockName) {
  return require(`./mocks/${mockName}.json`);
}

function* setHeaderObject(
  apiUrl,
  method,
  payload,
  pathParams = "",
  pathTemplate = "",
  additionalParams = ""
) {
  document.getElementById("loading-indicator").style.display = "block";
  const token = yield call(getAuthCredentials);
  let invokeUrl = baseURL + "/" + apiUrl;

  if (process.env.REACT_APP_AWS_ENV !== "PRODUCTION" && !window.Cypress) {
    const mockDataMap = {
      initialVerify: () => getMockData("experian/initialVerify"),
      finalVerify: () => getMockData("experian/finalVerify"),
    };

    if (mockDataMap[apiUrl]) return mockDataMap[apiUrl]();

    if (apiUrl === "VWGoARequestReport" && payload.confirmationNumber.startsWith("VWGOA_AAABB")) {
      // eslint-disable-next-line default-case
      switch (payload.reportType) {
        case "pdf":
          return getMockData("VWGoA/RequestReport/PDF");
        case "json":
          return getMockData("VWGoA/RequestReport/JSON");
      }
    }

    if (apiUrl === "VWGoARequestStatus") {
      const vwgoaMockDataMap = {
        VWGOA_AAABBCOR: { ...getMockData("VWGoA/RequestStatus/correctData"), ProcessingStatus: "REQ_COMPLETE" },
        VWGOA_AAABBCNF: { ...getMockData("VWGoA/RequestStatus/correctData"), ProcessingStatus: "SUBJECT_NOT_FOUND" },
        VWGOA_AAABBCIP: { ...getMockData("VWGoA/RequestStatus/correctData"), ProcessingStatus: "IN_PROCESS" },
        VWGOA_AAABBCRC: { ...getMockData("VWGoA/RequestStatus/correctData"), ProcessingStatus: "REQ_CANCELED" },
        VWGOA_AAABBDEL: { ...getMockData("VWGoA/RequestStatus/delete"), ProcessingStatus: "REQ_COMPLETE" },
        VWGOA_AAABBDNF: { ...getMockData("VWGoA/RequestStatus/delete"), ProcessingStatus: "SUBJECT_NOT_FOUND" },
        VWGOA_AAABBDIP: { ...getMockData("VWGoA/RequestStatus/delete"), ProcessingStatus: "IN_PROCESS" },
        VWGOA_AAABBDRC: { ...getMockData("VWGoA/RequestStatus/delete"), ProcessingStatus: "REQ_CANCELED" },
        VWGOA_AAABBOPT: { ...getMockData("VWGoA/RequestStatus/optOut"), ProcessingStatus: "REQ_COMPLETE" },
        VWGOA_AAABBONF: { ...getMockData("VWGoA/RequestStatus/optOut"), ProcessingStatus: "SUBJECT_NOT_FOUND" },
        VWGOA_AAABBOIP: { ...getMockData("VWGoA/RequestStatus/optOut"), ProcessingStatus: "IN_PROCESS" },
        VWGOA_AAABBORC: { ...getMockData("VWGoA/RequestStatus/optOut"), ProcessingStatus: "REQ_CANCELED" },
        VWGOA_AAABBRT1: { ...getMockData("VWGoA/RequestStatus/tier1"), ProcessingStatus: "REQ_COMPLETE" },
        VWGOA_AAABB1NF: { ...getMockData("VWGoA/RequestStatus/tier1"), ProcessingStatus: "SUBJECT_NOT_FOUND" },
        VWGOA_AAABB1IP: { ...getMockData("VWGoA/RequestStatus/tier1"), ProcessingStatus: "IN_PROCESS" },
        VWGOA_AAABB1RC: { ...getMockData("VWGoA/RequestStatus/tier1"), ProcessingStatus: "REQ_CANCELED" },
        VWGOA_AAABBRT2: { ...getMockData("VWGoA/RequestStatus/tier2"), ProcessingStatus: "REQ_COMPLETE" },
        VWGOA_AAABB2NF: { ...getMockData("VWGoA/RequestStatus/tier2"), ProcessingStatus: "SUBJECT_NOT_FOUND" },
        VWGOA_AAABB2IP: { ...getMockData("VWGoA/RequestStatus/tier2"), ProcessingStatus: "IN_PROCESS" },
        VWGOA_AAABB2RC: { ...getMockData("VWGoA/RequestStatus/tier2"), ProcessingStatus: "REQ_CANCELED" },
        VWGOA_AAABBSVF: getMockData("VWGoA/RequestStatus/successTrue_VerifiedFalse"),
        VWGOA_AAABBSFF: getMockData("VWGoA/RequestStatus/successFalse"),
      };

      if(vwgoaMockDataMap[payload.confirmationNumber]) {
        return vwgoaMockDataMap[payload.confirmationNumber];
      }
    }
  }

  if(payload.confirmationNumber?.startsWith("VWGOA_")) {
    additionalParams = {
      headers: {
        'VWGoA-Request-Id': guid(),
      }
    };
  }
  
  const apigClient = apigClientFactory.newClient({
    invokeUrl: invokeUrl,
    accessKey: token.accessKeyId,
    secretKey: token.secretAccessKey,
    sessionToken: token.sessionToken,
    region: process.env.REACT_APP_AWS_REGION,
  });
  const result = yield call(
    apigClient.invokeApi,
    pathParams,
    pathTemplate,
    method,
    additionalParams,
    payload
  );
  return result;
}

function* getAddressValidation(action) {
  try {
    const result = yield* setHeaderObject(
      "validateaddress",
      "POST",
      action.payload.payload
    );
    document.getElementById("loading-indicator").style.display = "none";
    if (result) {
      if (action.payload.payload.requestType === "RIGHT_TO_CORRECT") {        
        let body = result.data.body;
        let response;
        if (typeof body === "string") {
            response = JSON.parse(body);
        } else {
            response = body; // already an object
        }
        yield put({
          type: T.RIGHT_TO_CORRECT
            .SUGGESTED_CORRECTED_ADDRESS_REQUESTED_SUCCESS,
          payload: {
            ...response,
            state: response.stateOrProvince
          }
        });
      } else {
        let body = result.data.body;
        let response;
        if (typeof body === "string") {
            response = JSON.parse(body);
        } else {
            response = body; // already an object
        }
        yield put({
          type: T.SUGGESTED_MAILING_ADDRESS_REQUESTED_SUCCESS,
          payload:  {
            ...response,
            state: response.stateOrProvince
          },
        });
      }
      action.payload.handleAddressValidation(action.payload.payload);
      Logger.info(
        "strikeIronAddressValidation => Success",
        "Location ==> /mailingAddress",
        "SUCCESS"
      );
    }
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    if (action.payload.payload.requestType === "RIGHT_TO_CORRECT") {
      yield put({
        type: T.RIGHT_TO_CORRECT.SUGGESTED_CORRECTED_ADDRESS_REQUESTED_FAIL,
        payload: err,
      });
    } else {
      yield put({
        type: T.SUGGESTED_MAILING_ADDRESS_REQUESTED_FAIL,
        payload: err,
      });
    }
    const error = err.response ? err.response.status : err;
    if (error === 400) {
      if (action.payload.payload.requestType === "RIGHT_TO_CORRECT") {
        yield put({
          type: T.RIGHT_TO_CORRECT.SUGGESTED_CORRECTED_ADDRESS_REQUESTED_FAIL,
          payload: err.response.status,
        });
      } else {
        yield put({
          type: T.SUGGESTED_MAILING_ADDRESS_REQUESTED_FAIL,
          payload: err.response.status,
        });
      }
      action.payload.handleAddressValidation(action.payload.payload);
    } else {
      localStorage.setItem("path", "/");
      yield put({
        type: NETWORK_ERROR,
        payload: { error: err },
      });
    }
    Logger.error(
      "strikeIronAddressValidation => Fail",
      "Location ==> /mailingAddress",
      err
    );
    Logger.error(
      `Unsuccessful EsbRequest for a new request due to strikeIronAddressValidation API fail`,
      `Location ==> ${window.location.pathname}`,
      err
    );
  }
}

function* getPortalVerificationDetails(action) {
  try {
    const response = yield* setHeaderObject(
      action.isVWGoAConfirmationNumber? "VWGoARequestStatus" : "verify",
      "POST",
      action.payload.payload
    );
    document.getElementById("loading-indicator").style.display = "none";

    if(action.isVWGoAConfirmationNumber) {
      if (response.data.isSuccess === false)
        throw new Error("VWGOA Request with isSuccess = false");
      if (!response.data.processingStatus) 
        throw new Error("VWGOA Request with processingStatus is undefined");
      if (!response.data?.requestType)
        throw new Error("VWGOA Request with requestType missing = false");
      
      const requestType = action.payload.payload.requestType;
      if (requestType === "DELETE" && response.data?.requestType !== "DELETE")
        throw new Error("VWGOA Request with requestType is not DELETE");
      if (requestType === "DO_NOT_SELL" && response.data?.requestType !== "DO_NOT_SELL")
        throw new Error("VWGOA Request with requestType is not DO_NOT_SELL");
      if (requestType === "RIGHT_TO_DATA_CORRECTION" && response.data?.requestType !== "CORRECT_DATA")
        throw new Error("VWGOA Request with requestType is not CORRECT_DATA");
      if (requestType === "TIER1_RPT" && response.data?.requestType !== "TIER1_RPT")
        throw new Error("VWGOA Request with requestType is not TIER1_RPT");
      if (requestType === "TIER2_RPT" && response.data?.requestType !== "TIER2_RPT")
        throw new Error("VWGOA Request with requestType is not TIER2_RPT");
    }

    yield put({
      type: T.VERIFICATION_FORM.VERIFICATION_FORM_SUCCESS,
      payload: response.data,
    });
    Logger.info(
      "getPortalVerificationDetails => Success",
      "Location ==> /verificationForm",
      "SUCCESS"
    );
    const url = `/status`;
    action.payload.props.history.push(url, {
      isVWGoAConfirmationNumber: action.isVWGoAConfirmationNumber,
      payload: action.payload.payload,
      response: { data : response.data },
    });
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    yield put({
      type: T.VERIFICATION_FORM.VERIFICATION_FORM_FAIL,
      payload: err,
    });
    yield put({
      type: NETWORK_ERROR,
      payload: { error: err },
    });
    const error = err.response ? err.response.status : err;
    if (err.message === "VWGOA Request with processingStatus is undefined") {
      yield put({
        type: USER_NOT_FOUND_ERROR,
        payload: { error: "Bad_UserRequestNotFound" },
      });
    }
    if (action.isVWGoAConfirmationNumber) {
      Logger.error(
        "getPortalVerificationDetails => Bad Request for brand VWGoA request",
        "Location ==> /verificationForm",
        err
      );
    } else if (error === 400) {
      if (err.response?.data?.Errors[0]?.Code === "Bad_UserRequestNotFound")
        yield put({
          type: USER_NOT_FOUND_ERROR,
          payload: { error: "Bad_UserRequestNotFound" },
        });
      Logger.error(
        "getPortalVerificationDetails => Bad Request",
        "Location ==> /verificationForm",
        err
      );
    } else {
      yield put({
        type: NETWORK_ERROR,
        payload: { error: err },
      });
      Logger.error(
        "getPortalVerificationDetails => Network Error",
        "Location ==> /verificationForm",
        err
      );
    }
    localStorage.setItem("path", "/");
  }
}

function* getPrivacyManagement(action) {
  const { payload } = action.payload;
  try {
    // If isVWGoAConfirmationNumber we're using previous response from getPortalVerificationDetails function
    const result = payload.isVWGoAConfirmationNumber? payload.response : yield* setHeaderObject(
      payload.isVWGoAConfirmationNumber? "VWGoARequestStatus" : "management",
      "POST",
      payload
    );
    let data = result.data;

    if (result) {
      result.data.vwgoaDowngradeInd = false;
      result.data.downGradeDate = undefined;

      if(payload.isVWGoAConfirmationNumber) {
        result.data.requestType = data.requestType;
        result.data.requestDate = data.requestSubmitDate || data.requestDate;
        switch(data.processingStatus) {
          case "REQ_COMPLETE":
            result.data.combinedStatus = 'completed';
            break;
          case "SUBJECT_NOT_FOUND":
            result.data.combinedStatus = 'NotFound';
            break;
          case "IN_PROCESS":
            result.data.combinedStatus = "Pending";
            break;
          case "REQ_CANCELED":
            result.data.combinedStatus = 'Canceled'
            break;
          default:
            result.data.combinedStatus = "Pending";
        }
      } else if (
        result.data.combinedStatus.toLowerCase() === "completed" &&
        (result.data.requestType === "TIER1_RPT" ||
          result.data.requestType === "TIER2_RPT")
      ) {
        const resultDocStore = yield* getDocStoreReport(action);
        const { data: docStoreData } = resultDocStore;
        if (docStoreData) {
          if (docStoreData.documents.length === 0) {
            if (payload.isProtectionServices && result.data.vciRequestStatus === 'Canceled') {
              result.data.combinedStatus = 'Canceled';
            } else if (payload.isProtectionServices && result.data.vciRequestStatus === 'NotFound') {
              result.data.combinedStatus = 'NotFound';
            } else if (result.data.vciRequestStatus === 'Canceled' && result.data.vwgoaRequestStatus === 'Canceled') {
              result.data.combinedStatus = 'Canceled';
            } else {
              result.data.combinedStatus = "Pending";
            }
          }
        }
        data = { ...data, docStoreData };
      }
      if (payload.isProtectionServices) {
        if (result.data.vciRequestStatus === 'Canceled') {
          result.data.combinedStatus = 'Canceled';
        } else if (result.data.vciRequestStatus === 'NotFound') {
          result.data.combinedStatus = 'NotFound';
        }
      } else if (result.data.vciRequestStatus === 'Canceled' && result.data.vwgoaRequestStatus === 'Canceled') {
        result.data.combinedStatus = 'Canceled';
      }
      document.getElementById("loading-indicator").style.display = "none";
      yield put({
        type: T.STATUS_FORM.STATUS_FORM_SUCCESS,
        payload: data,
      });
      action.payload.setStatusFormDetails(result.data);
      Logger.info(
        "getPrivacyManagement => Success",
        "Location ==> /status",
        "SUCCESS"
      );
    }
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    localStorage.setItem("path", "/");
    yield put({
      type: NETWORK_ERROR,
      payload: { error: err },
    });
    Logger.error("getPrivacyManagement => Fail", "Location ==> /status", err);
  }
}

function* submitPrivacyRequest(action) {
  const requestType = action.payload.payload.requestType;
  try {
    const result = yield* setHeaderObject(
      "esbrequest",
      "POST",
      action.payload.payload
    );
    document.getElementById("loading-indicator").style.display = "none";
    if (result) {
      action.payload.routeProps();
    }
    yield put({
      type: T.EXPERIAN_FORM.VERIFY_EXPERIAN_SUCCESS,
      payload: "",
    });
    Logger.info(
      `Successful EsbRequest for ${requestType}`,
      `Location ==> ${window.location.pathname}`,
      "SUCCESS"
    );
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    localStorage.setItem("path", "/");
    yield put({
      type: NETWORK_ERROR,
      payload: { error: err },
    });
    Logger.error(
      `Unsuccessful EsbRequest for ${requestType}`,
      `Location ==> ${window.location.pathname}`,
      err
    );
  }
}

function* getDocStoreReport(action) {
  try {
    document.getElementById("loading-indicator").style.display = "block";
    const validateDocAuth = yield* setHeaderObject(
      "authorize",
      "POST",
      action.payload.payload
    );
    if (validateDocAuth.status === 200) {
      const result = yield* setHeaderObject(
        "documentstore",
        "POST",
        action.payload.payload
      );
      Logger.info(
        "getDocStoreReport => Success",
        "Location ==> /status",
        "SUCCESS"
      );
      return result;
    } else if (validateDocAuth.status === 401) {
      localStorage.setItem("path", "/");
      yield put({
        type: NETWORK_ERROR,
        payload: { error: "API Error" },
      });
      Logger.error(
        "getDocStoreReport => Fail",
        "Location ==> /status",
        "API Error with status code 401"
      );
    }
    document.getElementById("loading-indicator").style.display = 'none';
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    localStorage.setItem("path", "/");
    yield put({
      type: NETWORK_ERROR,
      payload: { error: err },
    });
    Logger.error(
      "getDocStoreReport => Fail",
      "Location ==> /status",
      "FAIL",
      err
    );
  }
}

function* getVWGoADocument(action) {
  try {
    document.getElementById("loading-indicator").style.display = "block";
    const result = yield* setHeaderObject(
      "VWGoARequestReport",
      "POST",
      action.payload.payload
    );
    document.getElementById("loading-indicator").style.display = 'none';
    if (result.status === 200) {
      Logger.info(
        "getVWGoADocument => Success",
        "Location ==> /status",
        "SUCCESS"
      );
      const { convertDocument } = action.payload;
      convertDocument(result.data.reportData);
      return result;
    } else {
      yield put({
        type: NETWORK_ERROR,
        payload: { error: "API Error" },
      });
      Logger.error(
        "getVWGoADocument => Fail",
        "Location ==> /status",
        "API Error with status code 401"
      );
    }
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    localStorage.setItem("path", "/");
    const error = err.response ? err.response.status : err;
    if (error === 404) {
        yield put({
          type: NETWORK_ERROR,
          payload: { error: error },
        });
    } else {
      yield put({
        type: NETWORK_ERROR,
        payload: { error: err },
      });
    }
    Logger.error(
      "getVWGoADocument => Fail",
      "Location ==> /status",
      "FAIL",
      err
    );
  }
}

function* getExperianQuestions(action) {
  try {
    const result = yield* setHeaderObject(
      "initialVerify",
      "POST",
      action.payload.payload
    );
    document.getElementById("loading-indicator").style.display = "none";
    yield put({
      type: T.EXPERIAN_FORM.GET_EXPERIAN_SUCCESS,
      payload: result,
    });
    action.payload.routeProps.push("/experianForm", {
      submitPrivacyRequestPayload: action.payload.submitPrivacyRequestPayload,
    });
    Logger.info(
      "getExperianQuestions => Success",
      `Location ==> ${window.location.pathname}`,
      "SUCCESS"
    );
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    yield put({
      type: T.EXPERIAN_FORM.GET_EXPERIAN_FAIL,
      payload: err,
    });
    const error = err.response?.status || err;
    const errorDecisionCode =
      err?.response?.data?.experianResponseCodes?.errorDecisionCode || "";
    const errorQkbCode =
      err?.response?.data?.experianResponseCodes?.errorQkbCode || "";
    if (error === 400) {
      if (errorQkbCode === 9) {
        const submitPrivacyRequestPayload =
          action.payload.submitPrivacyRequestPayload;

        const last4Ssn =
          submitPrivacyRequestPayload.requester.fullSsn.slice(-4);

        const userNotFoundAction = {
          ...submitPrivacyRequestPayload,
          requester: {
            ...submitPrivacyRequestPayload.requester,
            fullSsn: "",
            last4Ssn: last4Ssn,
          },
          requestType: submitPrivacyRequestPayload.requestType === "TIER2_RPT" ? "TIER1_RPT" : submitPrivacyRequestPayload.requestType,
        };

        const result = yield* setHeaderObject(
          "esbrequest",
          "POST",
          userNotFoundAction
        );

        document.getElementById("loading-indicator").style.display = "none";

        if (result.data.isSuccess) {
          const info = {
            firstName: userNotFoundAction.requester.firstName,
            lastName: userNotFoundAction.requester.lastName,
          };

          const isTier1FormComplete = userNotFoundAction.requestType === "TIER1_RPT";
          const isDataCorrectionFormComplete = userNotFoundAction.requestType === "CORRECT_DATA";

          if (isTier1FormComplete) {
            action.payload.routeProps.push("/formComplete", {
              info,
              isTier1FormComplete,
            });
          }
          if (isDataCorrectionFormComplete) {
            action.payload.routeProps.push("/formComplete", {
              info,
              isDataCorrectionFormComplete,
            });
          }
        }
      }
      if (
        errorQkbCode === 2 ||
        errorQkbCode === 7 ||
        errorDecisionCode === "258" ||
        errorDecisionCode === "259" ||
        errorDecisionCode === "352" ||
        errorDecisionCode === "358" ||
        errorDecisionCode === "403" ||
        errorDecisionCode === "720" ||
        errorDecisionCode === "710"
      ) {
        yield put({
          type: NETWORK_ERROR,
          payload: { error: err },
        });
      }
      if (
        !errorQkbCode === 9 ||
        !errorQkbCode === 2 ||
        !errorQkbCode === 7 ||
        !errorDecisionCode === "258" ||
        !errorDecisionCode === "259" ||
        !errorDecisionCode === "352" ||
        !errorDecisionCode === "358" ||
        !errorDecisionCode === "403" ||
        !errorDecisionCode === "720" ||
        !errorDecisionCode === "710"
      ) {
        action.payload.routeProps.push("/pinForm", {
          submitPrivacyRequestPayload:
            action.payload.submitPrivacyRequestPayload,
        });
      }
      Logger.error(
        "getExperianQuestions => No Questions Returned",
        `Location ==> ${window.location.pathname}`,
        err
      );
    } else {
      yield put({
        type: NETWORK_ERROR,
        payload: { error: err },
      });
      Logger.error(
        `Unsuccessful EsbRequest for a new request due to getExperianQuestions API fail`,
        `Location ==> ${window.location.pathname}`,
        err
      );
    }
  }
}

function* verifyExperianQuestions(action) {
  try {
    const result = yield* setHeaderObject(
      "finalVerify",
      "POST",
      action.payload.payload
    );
    document.getElementById("loading-indicator").style.display = "none";
    yield put({
      type: T.EXPERIAN_FORM.VERIFY_EXPERIAN_SUCCESS,
      payload: result,
    });
    Logger.info(
      "verifyExperian => Success",
      "Location ==> /experianform",
      "SUCCESS"
    );
    action.payload.routeProps.push("/pinForm", {
      submitPrivacyRequestPayload: action.payload.submitPrivacyRequestPayload,
    });
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";

    yield put({
      type: T.EXPERIAN_FORM.VERIFY_EXPERIAN_FAIL,
      payload: err,
    });

    yield put({
      type: NETWORK_ERROR,
      payload: { error: err },
    });

    Logger.error(
      `Unsuccessful EsbRequest for a new request due to verifyExperian API fail`,
      `Location ==> ${window.location.pathname}`,
      err
    );
  }
}

function* secureKeyGetUserInfo(action) {
  try {
    const result = yield* setHeaderObject(
      "secureKeyGetUserInfo",
      "POST",
      action.payload.payload
    );
    
    document.getElementById("loading-indicator").style.display = "none";
    yield put({
      type: T.SECURE_KEY.GET_USER_SUCCESS,
      payload: result.data,
    });
    Logger.info(
      "SecureKeyGetUserInfo => Success",
      "Location ==> /callback",
      "SUCCESS"
    );
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    if (err.response?.data) {
      yield put({
        type: T.SECURE_KEY.GET_USER_FAIL,
        payload: { ...err.response.data }
      });
    } else {
      yield put({
        type: NETWORK_ERROR,
        payload: { error: err },
      });
    }

    Logger.error(
      `Unsuccessful SecureKey GetUserInfo request`,
      `Location ==> ${window.location.pathname}`,
      err
    );
  }
}

function* redirectToSecureKey(action) {
  action.payload.handleWhileLoadingSecureKeyUrl();
  try {
    const result = yield* setHeaderObject("secureKeyUrl", "POST", action.payload.payload);

    if (result) {
      Logger.info("strikeIronGetSecureKeyUrl => Success", "Location ==> /requestselector", "SUCCESS");
      window.location.href = result.data.url;
    }
    
  } catch (err) {
    document.getElementById("loading-indicator").style.display = 'none';

    const error = err.response ? err.response.status : err;
    action.payload.handleErrorsWhenLoadingSecureKeyUrl(error);

    Logger.info("strikeIronGetSecureKeyUrl => Fail", "Location ==> /requestselector", err);
    Logger.error(`Unsuccessful redirectToSecureKey due to strikeIronGetSecureKeyUrl API fail`, `Location ==> ${window.location.pathname}`, err);
  }
}

function* secureKeyGetPortalVerificationDetails(action, props) {
  try {
    const { confirmationNumber } = action.payload.payload;
    const response = yield* setHeaderObject(
      "verifyCanada",
      "POST",
      action.payload.payload
    );
    document.getElementById("loading-indicator").style.display = "none";
    yield put({
      type: T.SECURE_KEY.VERIFICATION_FORM_SUCCESS,
      payload: response.data,
    });
    Logger.info(
      "secureKeyGetPortalVerificationDetails => Success",
      "Location ==> /verificationForm",
      "SUCCESS"
    );
    if(response?.data?.requestId && response?.data?.pin) {
      const url = `/status`;
      const { requestId, pin } = response.data;
      action.payload.props.history.push(url, { requestId, confirmationNumber, pin });
    }
  } catch (err) {
    document.getElementById("loading-indicator").style.display = "none";
    yield put({
      type: T.SECURE_KEY.VERIFICATION_FORM_FAIL,
      payload: err,
    });
    yield put({
      type: NETWORK_ERROR,
      payload: { error: err },
    });
    const error = err.response ? err.response.status : err;
    if (error === 400) {
      if (err.response?.data?.Errors[0]?.Code === "Bad_UserRequestNotFound")
        yield put({
          type: USER_NOT_FOUND_ERROR,
          payload: { error: "Bad_UserRequestNotFound" },
        });
      Logger.error(
        "secureKeyGetPortalVerificationDetails => Bad Request",
        "Location ==> /verificationForm",
        err
      );
    } else {
      yield put({
        type: NETWORK_ERROR,
        payload: { error: err },
      });
      Logger.error(
        "secureKeyGetPortalVerificationDetails => Network Error",
        "Location ==> /verificationForm",
        err
      );
    }
    localStorage.setItem("path", "/");
  }
}


function* getProtectionServicesExperianQuestions(action) {
  try {
    const result = yield* setHeaderObject("initialVerifyAndSaveForSubmitProtectionServices", "POST", action.payload.submitPrivacyRequestPayload);
    document.getElementById("loading-indicator").style.display = 'none';

    yield put({
      type: T.EXPERIAN_FORM.GET_EXPERIAN_SUCCESS,
      payload: result
    });
    action.payload.routeProps.push("/experianForm", { submitPrivacyRequestPayload: action.payload.submitPrivacyRequestPayload });
    Logger.info("getProtectionServicesExperianQuestions => Success", `Location ==> ${window.location.pathname}`, "SUCCESS");
  } catch (err) {
    document.getElementById("loading-indicator").style.display = 'none';
    yield put({
      type: T.EXPERIAN_FORM.GET_EXPERIAN_FAIL,
      payload: err
    });
    yield put({
      type: NETWORK_ERROR,
      payload: { error: err?.response || err || "error in initialVerifyAndSaveForSubmitProtectionServices" }
    });
    Logger.error(`Unsuccessful EsbRequest for a new request due to getProtectionServicesExperianQuestions API fail`, `Location ==> ${window.location.pathname}`, err);
  }
}

function* verifyProtectionServicesExperianQuestions(action) {
  try {
    const result = yield* setHeaderObject("finalVerifyAndSubmitProtectionServices", "POST", action.payload.payload);
    document.getElementById("loading-indicator").style.display = 'none';
    yield put({
      type: T.PROTECTION_SERVICES_EXPERIAN_FORM.VERIFY_PROTECTION_SERVICES_EXPERIAN_SUCCESS,
      payload: result
    });

    Logger.info("ProtectionServices verifyExperian => Success", "Location ==> /experianform", "SUCCESS");

    if (result?.data?.isExperianVerified) {
      action.payload.routeProps();

      yield put({
        type: T.EXPERIAN_FORM.VERIFY_EXPERIAN_SUCCESS,
        payload: ""
      });
    } else {
      yield put({
        type: T.PROTECTION_SERVICES_EXPERIAN_FORM.VERIFY_PROTECTION_SERVICES_EXPERIAN_FAIL,
        payload: "isExperianVerified is false"
      });

      yield put({
        type: NETWORK_ERROR,
        payload: { error: result?.data || "error in finalVerifyAndSubmitProtectionServices" }
      });
      Logger.info("ProtectionServices verifyExperian => Success but isExperianVerified flag is false", "Location ==> /experianform", "SUCCESS");
    }
  } catch (err) {
    document.getElementById("loading-indicator").style.display = 'none';

    yield put({
      type: T.EXPERIAN_FORM.VERIFY_EXPERIAN_FAIL,
      payload: err
    });

    yield put({
      type: NETWORK_ERROR,
      payload: { error: err }
    });

    Logger.error(`Unsuccessful EsbRequest for a new request due to ProtectionServices verifyExperian API fail`, `Location ==> ${window.location.pathname}`, err);
  }
}

function* getProtectionServicesExperianQuestionsCheckMyRequests(action) {
  try {
    const result = yield* setHeaderObject("initialVerifyUserAndSaveForCheckMyStatusProtectionServices", "POST", action.payload.payload);
    document.getElementById("loading-indicator").style.display = 'none';
    yield put({
      type: T.EXPERIAN_FORM.GET_EXPERIAN_SUCCESS,
      payload: { ...result, payload: action.payload.payload }
    });
    action.payload.routeProps.push("/experianForm", { payload: action.payload.payload });
    Logger.info("getProtectionServicesExperianQuestionsCheckMyRequests => Success", `Location ==> ${window.location.pathname}`, "SUCCESS");
  } catch (err) {
    document.getElementById("loading-indicator").style.display = 'none';
    yield put({
      type: T.EXPERIAN_FORM.GET_EXPERIAN_FAIL,
      payload: err
    });
    yield put({
      type: NETWORK_ERROR,
      payload: { error: err?.response || err || "error in initialVerifyUserAndSaveForCheckMyStatusProtectionServices" }
    });
    Logger.error(`Unsuccessful EsbRequest for a new request due to getProtectionServicesExperianQuestionsCheckMyRequests API fail`, `Location ==> ${window.location.pathname}`, err);
  }
}

function* verifyProtectionServicesExperianQuestionsCheckMyRequests(action) {
  try {

    const result = yield* setHeaderObject("finalVerifyUserAndCheckMyStatusProtectionServices", "POST", action.payload.payload);
    document.getElementById("loading-indicator").style.display = 'none';
    yield put({
      type: T.PROTECTION_SERVICES_EXPERIAN_FORM.VERIFY_PROTECTION_SERVICES_EXPERIAN_SUCCESS,
      payload: result
    });

    Logger.info("ProtectionServices verifyExperian => Success", "Location ==> /experianform", "SUCCESS");
    if ((result?.data?.isExperianVerified || result?.data?.IsExperianVerified) && (result?.data?.isUserVerified || result?.data?.IsUserVerified)) {
      const url = "/status";
      action.payload.historyProps.push(url);
      yield put({
        type: T.STATUS_FORM.STATUS_FORM_DETAILS,
        payload: {
          payload: {
            requestId: result?.data?.user?.requestId || result?.data?.User?.requestId,
            confirmationNumber: result?.data?.user?.confirmationNumber || result?.data?.User?.confirmationNumber,
            pin: result?.data?.user?.pin ? result?.data?.user?.pin : "",
            isProtectionServices: true,
          }
        }
      });
      Logger.info("getPortalVerificationDetails => Success", "Location ==> /verificationForm", "SUCCESS");
    } else {
      yield put({
        type: T.PROTECTION_SERVICES_EXPERIAN_FORM.VERIFY_PROTECTION_SERVICES_EXPERIAN_FAIL,
        payload: "isExperianVerified is false"
      });

      yield put({
        type: NETWORK_ERROR,
        payload: { error: result?.data || "error in finalVerifyUserAndCheckMyStatusProtectionServices" }
      });
      Logger.info("ProtectionServices verifyExperian => Success but isExperianVerified flag is false", "Location ==> /experianform", "SUCCESS");
    }
  } catch (err) {
    document.getElementById("loading-indicator").style.display = 'none';

    yield put({
      type: T.EXPERIAN_FORM.VERIFY_EXPERIAN_FAIL,
      payload: err
    });

    yield put({
      type: NETWORK_ERROR,
      payload: { error: err?.response?.data || err || "error in finalVerifyUserAndCheckMyStatusProtectionServices" }
    });

    Logger.error(`Unsuccessful EsbRequest for a new request due to ProtectionServices verifyExperian API fail`, `Location ==> ${window.location.pathname}`, err);
  }
}

function* AuthSaga() {
  yield takeLatest(T.MAILING_ADDRESS_ENTERED, getAddressValidation);
  yield takeEvery(T.SECURE_KEY.GET_URL, redirectToSecureKey);
  yield takeEvery(T.SECURE_KEY.GET_USER_DETAILS, secureKeyGetUserInfo);
  yield takeEvery(T.SECURE_KEY.VERIFICATION_FORM_DETAILS, secureKeyGetPortalVerificationDetails);
  yield takeEvery(T.VERIFICATION_FORM.VERIFICATION_FORM_DETAILS, getPortalVerificationDetails);
  yield takeLatest(T.SUBMIT_PRIVACY_REQUEST, submitPrivacyRequest);
  yield takeLatest(T.STATUS_FORM.STATUS_FORM_DETAILS, getPrivacyManagement);
  yield takeEvery(T.STATUS_FORM.GET_DOC_STORE_REPORT, getDocStoreReport);
  yield takeEvery(T.STATUS_FORM.GET_VWGOA_DOCUMENT, getVWGoADocument);
  yield takeLatest(T.EXPERIAN_FORM.GET_EXPERIAN_DETAILS, getExperianQuestions);
  yield takeLatest(T.EXPERIAN_FORM.VERIFY_EXPERIAN_DETAILS, verifyExperianQuestions);
  yield takeLatest(T.PROTECTION_SERVICES_EXPERIAN_FORM.GET_PROTECTION_SERVICES_EXPERIAN_DETAILS, getProtectionServicesExperianQuestions);
  yield takeLatest(T.PROTECTION_SERVICES_EXPERIAN_FORM.VERIFY_PROTECTION_SERVICES_EXPERIAN_DETAILS, verifyProtectionServicesExperianQuestions);
  yield takeLatest(T.PROTECTION_SERVICES_EXPERIAN_FORM.GET_PROTECTION_SERVICES_EXPERIAN_DETAILS_CHECK_MY_REQUESTS, getProtectionServicesExperianQuestionsCheckMyRequests);
  yield takeLatest(T.PROTECTION_SERVICES_EXPERIAN_FORM.VERIFY_PROTECTION_SERVICES_EXPERIAN_DETAILS_CHECK_MY_REQUESTS, verifyProtectionServicesExperianQuestionsCheckMyRequests);
}

export default AuthSaga;